/**
 * Katie & Laura — Save to scrapbook (shop photos)
 *
 * Writes Customer metafield diary.notes_photos via App Proxy:
 *   POST {proxy}/notes-photos
 *   { action: 'add_photo', url, caption?, source: 'user', productId?, handle?, title?, addedAt? }
 * Soft-fail 404 until the Worker route lands.
 * Guest: store pending photo, sign in, resume.
 * Success toast is plain text: Photo saved to Diary (no HTML, no page link).
 * Optimistic cache: sessionStorage + localStorage `kl_notes_photos:{customerId}`.
 * Never put WISHLIST_PROXY_SECRET here.
 */
(function (global) {
  'use strict';

  var DEFAULT_PROXY = '/apps/kl-wishlist';
  var PENDING_KEY = 'kl_notes_photos_pending';
  var LONG_MS = 480;
  var MOVE_PX = 12;
  var pressTimer = null;
  var pressStart = null;
  var ignoreClick = false;

  function config() {
    return global.KLNotesPhotosConfig || {};
  }

  function strings() {
    var s = config().strings || {};
    return {
      save: s.save || 'Save to Diary',
      saved: s.saved || 'Photo saved to Diary',
      signIn: s.signIn || 'Sign in to keep this photo.',
      cancel: s.cancel || 'Cancel',
      error: s.error || 'Couldn’t save just now — try again in a moment.'
    };
  }

  function appProxyPath() {
    var raw = String(config().appProxyPath || '').trim();
    if (!raw) raw = DEFAULT_PROXY;
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function notesPhotosUrl() {
    var raw = String(config().notesPhotosUrl || '/pages/notes-photos').trim();
    if (!raw) raw = '/pages/notes-photos';
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw;
  }

  function loginReturnUrl() {
    var login = String(config().loginUrl || '/account/login').trim() || '/account/login';
    var dest = '';
    try {
      dest = (location.pathname || '') + (location.search || '') + (location.hash || '');
    } catch (e) {
      dest = notesPhotosUrl();
    }
    var sep = login.indexOf('?') >= 0 ? '&' : '?';
    return login + sep + 'return_to=' + encodeURIComponent(dest) + '&checkout_url=' + encodeURIComponent(dest);
  }

  function isCoarse() {
    try {
      return !!(global.matchMedia && global.matchMedia('(hover: none), (pointer: coarse)').matches);
    } catch (e) {
      return false;
    }
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function uid(prefix) {
    return prefix + '-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function normalizePhoto(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var url = String(raw.url || raw.image || '').trim();
    if (!url) return null;
    return {
      id: String(raw.id || uid('photo')),
      url: url,
      caption: raw.caption ? String(raw.caption) : '',
      source: raw.source === 'upload' ? 'upload' : 'user',
      addedAt: raw.addedAt ? String(raw.addedAt) : new Date().toISOString(),
      handle: raw.handle ? String(raw.handle) : '',
      title: raw.title ? String(raw.title) : '',
      productId: raw.productId ? String(raw.productId) : ''
    };
  }

  function readPending() {
    try {
      return normalizePhoto(JSON.parse(global.sessionStorage.getItem(PENDING_KEY) || 'null'));
    } catch (e) {
      return null;
    }
  }

  function writePending(photo) {
    try {
      if (!photo) global.sessionStorage.removeItem(PENDING_KEY);
      else global.sessionStorage.setItem(PENDING_KEY, JSON.stringify(photo));
    } catch (e) {}
  }

  function proxyPost(body) {
    return fetch(appProxyPath() + '/notes-photos', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-notes-photos ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function toastEl() {
    var el = document.querySelector('[data-kl-scrapbook-toast]');
    if (el) return el;
    el = document.createElement('p');
    el.className = 'kl-scrapbook-toast';
    el.setAttribute('data-kl-scrapbook-toast', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    document.body.appendChild(el);
    return el;
  }

  function showToast(message, isError) {
    var el = toastEl();
    el.hidden = false;
    el.classList.toggle('is-error', !!isError);
    el.textContent = message || '';
    clearTimeout(el._klTimer);
    el._klTimer = setTimeout(function () {
      el.hidden = true;
    }, 3200);
  }

  function savedToast() {
    showToast(strings().saved, false);
  }

  function sheetEls() {
    var root = document.querySelector('[data-kl-scrapbook-sheet]');
    if (root) return root;
    root = document.createElement('div');
    root.className = 'kl-scrapbook-sheet';
    root.setAttribute('data-kl-scrapbook-sheet', '');
    root.hidden = true;
    var copy = strings();
    root.innerHTML =
      '<button type="button" class="kl-scrapbook-sheet__dim" data-kl-scrapbook-dismiss aria-label="' +
      escapeHtml(copy.cancel) +
      '"></button>' +
      '<div class="kl-scrapbook-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="kl-scrapbook-sheet-title">' +
        '<p class="kl-scrapbook-sheet__title" id="kl-scrapbook-sheet-title">' +
          escapeHtml(copy.save) +
        '</p>' +
        '<img class="kl-scrapbook-sheet__preview" data-kl-scrapbook-preview alt="" width="800" height="600">' +
        '<div class="kl-scrapbook-sheet__actions">' +
          '<button type="button" class="kl-scrapbook-sheet__save" data-kl-scrapbook-sheet-save>' +
            escapeHtml(copy.save) +
          '</button>' +
          '<button type="button" class="kl-scrapbook-sheet__cancel" data-kl-scrapbook-dismiss>' +
            escapeHtml(copy.cancel) +
          '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(root);
    return root;
  }

  function hideSheet() {
    var root = document.querySelector('[data-kl-scrapbook-sheet]');
    if (root) {
      root.hidden = true;
      delete root.dataset.photo;
    }
  }

  function showSheet(photo) {
    if (!photo) return;
    var root = sheetEls();
    root.dataset.photo = JSON.stringify(photo);
    var img = root.querySelector('[data-kl-scrapbook-preview]');
    if (img) {
      img.src = photo.url;
      img.alt = photo.title || '';
    }
    root.hidden = false;
    ignoreClick = true;
    setTimeout(function () {
      ignoreClick = false;
    }, 400);
  }

  function visibleGalleryMedia(gallery) {
    if (!gallery) return null;
    var frame = gallery.querySelector('.product-gallery__carousel') || gallery;
    var nodes = frame.querySelectorAll('.product-gallery__media:not([hidden])');
    if (!nodes.length) return null;
    var gRect = frame.getBoundingClientRect();
    var best = nodes[0];
    var bestArea = -1;
    for (var i = 0; i < nodes.length; i++) {
      var r = nodes[i].getBoundingClientRect();
      var overlap =
        Math.max(0, Math.min(r.right, gRect.right) - Math.max(r.left, gRect.left)) *
        Math.max(0, Math.min(r.bottom, gRect.bottom) - Math.max(r.top, gRect.top));
      if (overlap > bestArea) {
        bestArea = overlap;
        best = nodes[i];
      }
    }
    return best;
  }

  function imageFromNode(node) {
    if (!node) return '';
    var marked = node.getAttribute && node.getAttribute('data-kl-scrapbook-src');
    if (marked) return marked;
    var img = node.querySelector && node.querySelector('img');
    if (img) return img.currentSrc || img.src || '';
    return '';
  }

  function photoFromButton(btn) {
    if (!btn) return null;
    var media = btn.closest && btn.closest('[data-kl-scrapbook-media]');
    var gallery = btn.closest && btn.closest('product-gallery, .product-gallery');
    if (!gallery) {
      var host = (btn.closest && (btn.closest('.product') || btn.closest('product-rerender'))) || document;
      gallery = host.querySelector && host.querySelector('product-gallery, .product-gallery');
    }
    var src = btn.getAttribute('data-image') || '';
    if (media) {
      src = imageFromNode(media) || src;
    } else if (gallery) {
      src = imageFromNode(visibleGalleryMedia(gallery)) || src;
    }
    return normalizePhoto({
      url: src,
      caption: btn.getAttribute('data-title') || '',
      handle: btn.getAttribute('data-handle') || '',
      title: btn.getAttribute('data-title') || '',
      productId: btn.getAttribute('data-product-id') || ''
    });
  }

  function photoFromMedia(media) {
    if (!media) return null;
    var localBtn = media.querySelector && media.querySelector('[data-kl-scrapbook-save]');
    var host =
      media.closest('[data-kl-scrapbook-product]') ||
      media.closest('.product-card, .horizontal-product-card, product-gallery, .product');
    var btn = localBtn || (host && host.querySelector && host.querySelector('[data-kl-scrapbook-save]'));
    var src = imageFromNode(media);
    if (btn) {
      var fromBtn = photoFromButton(btn);
      if (fromBtn) {
        if (src) fromBtn.url = src;
        return fromBtn;
      }
    }
    return normalizePhoto({
      url: src,
      handle: media.getAttribute('data-handle') || '',
      title: media.getAttribute('data-title') || '',
      productId: media.getAttribute('data-product-id') || ''
    });
  }

  function goLogin(photo) {
    writePending(photo);
    global.location.href = loginReturnUrl();
  }

  function cacheKey() {
    return 'kl_notes_photos:' + (config().customerId || 'anon');
  }

  function cacheDocHasItems(parsed) {
    if (!parsed || typeof parsed !== 'object') return false;
    return (
      (Array.isArray(parsed.photos) && parsed.photos.length) ||
      (Array.isArray(parsed.notes) && parsed.notes.length) ||
      (Array.isArray(parsed.hiddenStarterIds) && parsed.hiddenStarterIds.length)
    );
  }

  function readCacheDoc() {
    var key = cacheKey();
    var stores = [global.sessionStorage, global.localStorage];
    var fallback = {};
    for (var s = 0; s < stores.length; s++) {
      try {
        var parsed = JSON.parse(stores[s].getItem(key) || 'null');
        if (cacheDocHasItems(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') fallback = parsed;
      } catch (e) {}
    }
    return fallback;
  }

  function writeCacheDoc(doc) {
    var key = cacheKey();
    var raw = JSON.stringify(doc);
    try {
      global.sessionStorage.setItem(key, raw);
    } catch (e) {}
    try {
      global.localStorage.setItem(key, raw);
    } catch (e) {}
  }

  function rememberPhoto(photo) {
    try {
      var doc = readCacheDoc() || {};
      if (!Array.isArray(doc.notes)) doc.notes = [];
      if (!Array.isArray(doc.photos)) doc.photos = [];
      if (!Array.isArray(doc.hiddenStarterIds)) doc.hiddenStarterIds = [];
      var exists = false;
      for (var i = 0; i < doc.photos.length; i++) {
        if (doc.photos[i] && (doc.photos[i].id === photo.id || doc.photos[i].url === photo.url)) {
          exists = true;
          break;
        }
      }
      if (!exists) doc.photos.push(photo);
      writeCacheDoc(doc);
    } catch (e) {}
  }

  function savePhoto(photo, fromUser) {
    var next = normalizePhoto(photo);
    if (!next) return;
    if (!config().loggedIn) {
      if (fromUser) goLogin(next);
      return;
    }
    rememberPhoto(next);
    savedToast();
    var body = { action: 'add_photo', url: next.url, source: 'user' };
    if (next.caption) body.caption = next.caption;
    if (next.productId) body.productId = next.productId;
    if (next.handle) body.handle = next.handle;
    if (next.title) body.title = next.title;
    if (next.addedAt) body.addedAt = next.addedAt;
    proxyPost(body).catch(function () {
      /* 404 until Worker ships — toast + browser cache already written */
    });
  }

  function clearPress() {
    if (pressTimer) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
    pressStart = null;
  }

  function onPointerDown(event) {
    if (!isCoarse()) return;
    if (event.pointerType === 'mouse') return;
    var media = event.target && event.target.closest && event.target.closest('[data-kl-scrapbook-media]');
    if (!media) return;
    if (event.target.closest && event.target.closest('[data-kl-scrapbook-save], .kl-wishlist-btn, .product-card__quick-add-button')) {
      return;
    }
    clearPress();
    pressStart = { x: event.clientX, y: event.clientY, media: media };
    pressTimer = setTimeout(function () {
      pressTimer = null;
      if (!pressStart) return;
      showSheet(photoFromMedia(pressStart.media));
    }, LONG_MS);
  }

  function onPointerMove(event) {
    if (!pressStart) return;
    if (
      Math.abs(event.clientX - pressStart.x) > MOVE_PX ||
      Math.abs(event.clientY - pressStart.y) > MOVE_PX
    ) {
      clearPress();
    }
  }

  function onPointerUp() {
    clearPress();
  }

  function onContextMenu(event) {
    if (!isCoarse()) return;
    var media = event.target && event.target.closest && event.target.closest('[data-kl-scrapbook-media]');
    if (!media) return;
    event.preventDefault();
    if (!document.querySelector('[data-kl-scrapbook-sheet]:not([hidden])')) {
      showSheet(photoFromMedia(media));
    }
  }

  function onClick(event) {
    if (ignoreClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    var dismiss = event.target.closest && event.target.closest('[data-kl-scrapbook-dismiss]');
    if (dismiss) {
      event.preventDefault();
      hideSheet();
      return;
    }
    var sheetSave = event.target.closest && event.target.closest('[data-kl-scrapbook-sheet-save]');
    if (sheetSave) {
      event.preventDefault();
      var root = sheetSave.closest('[data-kl-scrapbook-sheet]');
      var photo = null;
      try {
        photo = root && root.dataset.photo ? JSON.parse(root.dataset.photo) : null;
      } catch (e) {
        photo = null;
      }
      hideSheet();
      savePhoto(photo, true);
      return;
    }
    var btn = event.target.closest && event.target.closest('[data-kl-scrapbook-save]');
    if (btn) {
      event.preventDefault();
      event.stopPropagation();
      savePhoto(photoFromButton(btn), true);
    }
  }

  function resumePending() {
    if (!config().loggedIn) return;
    var pending = readPending();
    if (!pending) return;
    writePending(null);
    savePhoto(pending, true);
  }

  function init() {
    resumePending();
  }

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerUp, true);
  document.addEventListener('contextmenu', onContextMenu, true);
  document.addEventListener('click', onClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.KLNotesScrapbook = {
    metafield: { namespace: 'diary', key: 'notes_photos' },
    savePhoto: savePhoto,
    photoFromButton: photoFromButton,
    photoFromMedia: photoFromMedia
  };
})(typeof window !== 'undefined' ? window : this);
